/**
 * CampagneDetailPage — wizard + stats voor één campagne.
 *
 * Flow: concept (segment/template/onderwerp) → audience-preview (allowed +
 * suppressie-breakdown) → indienen → (goedkeuren boven drempel) → versturen
 * of inplannen → live stats.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  marketingFetch,
  type AudiencePreview,
  type CampaignStats,
  type MarketingCampaign,
} from '../../stores/marketing-store';
import {
  MarketingNav,
  MarketingPageHeader,
  SectionCard,
  StatusBadge,
  SUPPRESSION_REASON_LABELS,
  formatDateTime,
} from './shared';
import { TestMailPanel } from './drawers';

interface CampaignDetail {
  campaign: MarketingCampaign;
  stats: CampaignStats;
}

function TemplatePreview({ templateId, contactId }: { templateId: string; contactId?: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const [subject, setSubject] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ id: templateId, preview: '1' });
    if (contactId) params.set('contact_id', contactId);
    marketingFetch<{ preview: { html: string; subject: string } }>(`/api/marketing/templates?${params}`)
      .then((data) => {
        if (cancelled) return;
        setHtml(data.preview.html);
        setSubject(data.preview.subject);
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Preview mislukt'));
    return () => {
      cancelled = true;
    };
  }, [templateId, contactId]);

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!html) return <p className="text-sm text-gray-400">Preview laden…</p>;
  return (
    <div>
      <p className="text-sm text-gray-600 mb-2">
        <span className="text-gray-400">Onderwerp:</span> <span className="font-medium text-gray-900">{subject}</span>
      </p>
      <iframe title="Template preview" srcDoc={html} className="w-full h-96 border border-gray-200 rounded-lg bg-white" sandbox="" />
    </div>
  );
}

export function CampagneDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<CampaignDetail | null>(null);
  const [preview, setPreview] = useState<AudiencePreview | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const data = await marketingFetch<CampaignDetail>(`/api/marketing/campaigns?id=${id}`);
      setDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (action: string) => {
    if (!id) return;
    setBusy(action);
    setError(null);
    try {
      const data = await marketingFetch<Record<string, unknown>>(`/api/marketing/campaigns?id=${id}&action=${action}`, {
        method: 'POST',
      });
      if (action === 'preview') {
        setPreview(data as unknown as AudiencePreview);
      } else {
        await load();
        if (action === 'submit' && (data as { audience?: AudiencePreview }).audience) {
          setPreview((data as { audience: AudiencePreview }).audience);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `${action} mislukt`);
    } finally {
      setBusy(null);
    }
  };

  const campaign = detail?.campaign;
  const stats = detail?.stats;
  const sentLike = campaign && ['sending', 'sent'].includes(campaign.status);

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <MarketingPageHeader
          title={campaign?.name ?? 'Campagne'}
          subtitle={campaign ? `Status: ${campaign.status}` : undefined}
          actions={campaign && <StatusBadge status={campaign.status} />}
        />
        <MarketingNav />

        <div className="p-6 space-y-6">
          <Link to="/marketing/campagnes" className="text-sm text-indigo-600 hover:underline">
            ← Alle campagnes
          </Link>

          {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}
          {!campaign ? (
            <p className="text-sm text-gray-400">Laden…</p>
          ) : (
            <>
              {/* Acties per status */}
              <SectionCard title="Acties">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => runAction('preview')}
                    disabled={busy !== null}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    {busy === 'preview' ? 'Berekenen…' : 'Audience-preview'}
                  </button>
                  {campaign.status === 'draft' && (
                    <button
                      onClick={() => runAction('submit')}
                      disabled={busy !== null}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {busy === 'submit' ? 'Indienen…' : 'Indienen'}
                    </button>
                  )}
                  {campaign.status === 'pending_approval' && (
                    <button
                      onClick={() => runAction('approve')}
                      disabled={busy !== null}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {busy === 'approve' ? 'Goedkeuren…' : `Goedkeuren (${campaign.audience_count ?? '?'} ontvangers)`}
                    </button>
                  )}
                  {campaign.status === 'approved' && (
                    <button
                      onClick={() => {
                        if (window.confirm(`Campagne versturen naar ${campaign.audience_count ?? '?'} ontvangers?`)) {
                          runAction('send');
                        }
                      }}
                      disabled={busy !== null}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {busy === 'send' ? 'Versturen…' : campaign.scheduled_at ? 'Inplannen' : 'Nu versturen'}
                    </button>
                  )}
                  {!['sent', 'cancelled'].includes(campaign.status) && (
                    <button
                      onClick={() => {
                        if (window.confirm('Campagne annuleren? Wachtende sends worden geannuleerd.')) {
                          runAction('cancel');
                        }
                      }}
                      disabled={busy !== null}
                      className="px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    >
                      Annuleren
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-3 space-x-3">
                  {campaign.scheduled_at && <span>Gepland: {formatDateTime(campaign.scheduled_at)}</span>}
                  {campaign.approved_by && (
                    <span>
                      Goedgekeurd door {campaign.approved_by} op {formatDateTime(campaign.approved_at)}
                    </span>
                  )}
                  {campaign.sent_at && <span>Verstuurd: {formatDateTime(campaign.sent_at)}</span>}
                </div>
              </SectionCard>

              {/* Audience preview */}
              {preview && (
                <SectionCard title="Audience">
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div>
                      <p className="text-2xl font-semibold text-gray-900">{preview.total}</p>
                      <p className="text-xs text-gray-400">in segment</p>
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-emerald-700">{preview.allowed}</p>
                      <p className="text-xs text-gray-400">ontvangen de mail</p>
                    </div>
                    <div>
                      <p className="text-2xl font-semibold text-amber-700">{preview.total - preview.allowed}</p>
                      <p className="text-xs text-gray-400">onderdrukt</p>
                    </div>
                  </div>
                  {Object.keys(preview.suppressed).length > 0 && (
                    <div className="text-sm text-gray-600 space-y-1 mb-4">
                      {Object.entries(preview.suppressed).map(([reason, count]) => (
                        <div key={reason} className="flex justify-between">
                          <span>{SUPPRESSION_REASON_LABELS[reason] ?? reason}</span>
                          <span className="font-medium">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {preview.sample.length > 0 && (
                    <p className="text-xs text-gray-400">
                      Steekproef: {preview.sample.map((s) => s.email).join(', ')}
                    </p>
                  )}
                </SectionCard>
              )}

              {/* Stats (na verzending) */}
              {sentLike && stats && (
                <SectionCard title="Resultaten">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {(['sent', 'delivered', 'bounced', 'suppressed', 'failed'] as const).map((status) => (
                      <div key={status}>
                        <p className="text-xl font-semibold text-gray-900">{stats.sends[status] ?? 0}</p>
                        <p className="text-xs text-gray-400 capitalize">{status}</p>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
                    {(['opened', 'clicked', 'complained', 'replied'] as const).map((event) => (
                      <div key={event}>
                        <p className="text-xl font-semibold text-gray-900">{stats.uniqueEvents[event] ?? 0}</p>
                        <p className="text-xs text-gray-400 capitalize">{event} (uniek)</p>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              )}

              {/* Template preview + testmail */}
              {campaign.template_id && (
                <SectionCard title="Template-preview">
                  <TemplatePreview templateId={campaign.template_id} />
                  <div className="mt-4 -mx-5 -mb-5">
                    <TestMailPanel templateId={campaign.template_id} subjectOverride={campaign.subject_override} />
                  </div>
                </SectionCard>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
